import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaTimes, FaRocket, FaBell, FaArrowRight } from 'react-icons/fa';

const float = keyframes`
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-5px);
  }
  100% {
    transform: translateY(0px);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(8px);
  z-index: 20000;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.3s ease;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContainer = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%);
  border-radius: 28px;
  width: 90%;
  max-width: 420px;
  position: relative;
  overflow: hidden;
  animation: slideUp 0.4s cubic-bezier(0.34, 1.2, 0.64, 1);
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  
  @keyframes slideUp {
    from {
      transform: translateY(30px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    max-width: 380px;
  }

  @media (max-width: 768px) {
    max-width: 90%;
    margin: 1rem;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: rgba(55, 48, 163, 0.1);
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #3730A3;
  transition: all 0.3s ease;
  z-index: 10;
  
  &:hover {
    background: #3730A3;
    color: white;
    transform: rotate(90deg);
  }
  
  svg {
    font-size: 1rem;
  }
`;

const TopDecoration = styled.div`
  background: linear-gradient(135deg, #3730A3 0%, #0b0b77 100%);
  height: 6px;
`;

const Content = styled.div`
  padding: 1.5rem 1.8rem 2rem;
  text-align: center;
`;

const IconWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, #3730A3 0%, #6366f1 100%);
  border-radius: 50%;
  margin-bottom: 1rem;
  animation: ${float} 2s ease-in-out infinite;
  box-shadow: 0 10px 25px -5px rgba(55, 48, 163, 0.3);
  
  svg {
    font-size: 2.2rem;
    color: white;
  }
`;

const Title = styled.h2`
  font-size: 1.6rem;
  font-weight: 800;
  background: linear-gradient(135deg, #3730A3 0%, #0b0b77 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 0.3rem;
  font-family: 'Times New Roman', serif;

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 1.4rem;
  }
`;

const Subtitle = styled.p`
  font-size: 0.85rem;
  color: #64748b;
  margin-bottom: 1rem;
  font-weight: 500;
`;

const Badge = styled.div`
  display: inline-block;
  background: rgba(55, 48, 163, 0.1);
  padding: 0.3rem 1rem;
  border-radius: 50px;
  font-size: 0.7rem;
  font-weight: 600;
  color: #3730A3;
  margin-bottom: 1.2rem;
  letter-spacing: 0.5px;
`;

const ProgressContainer = styled.div`
  background: #e2e8f0;
  border-radius: 20px;
  height: 6px;
  margin: 1.2rem 0;
  overflow: hidden;
`;

const ProgressBar = styled.div`
  width: 65%;
  height: 100%;
  background: linear-gradient(90deg, #3730A3, #a78bfa);
  border-radius: 20px;
  position: relative;
  overflow: hidden;
  
  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent
    );
    animation: ${shimmer} 1.5s infinite;
  }
`;

const ProgressText = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 0.3rem;
  font-size: 0.7rem;
  color: #64748b;
  font-weight: 500;
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  margin: 1.2rem 0;
  text-align: left;
  background: #f8fafc;
  padding: 0.8rem 1rem;
  border-radius: 16px;
`;

const Feature = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.78rem;
  color: #1e293b;
  font-weight: 500;
  
  svg {
    color: #3730A3;
    font-size: 0.75rem;
    flex-shrink: 0;
  }
`;

const NotifyButton = styled.button`
  background: linear-gradient(135deg, #3730A3 0%, #0b0b77 100%);
  color: white;
  border: none;
  padding: 0.7rem 1.2rem;
  border-radius: 50px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  transition: all 0.3s ease;
  margin-top: 0.8rem;
  font-family: 'Times New Roman', serif;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px -5px rgba(55, 48, 163, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  svg {
    font-size: 0.85rem;
  }
`;

const SmallText = styled.p`
  font-size: 0.65rem;
  color: #94a3b8;
  margin-top: 0.8rem;
  font-weight: 500;
`;

const ComingSoonModal = ({ isOpen, onClose }) => {
  const [notified, setNotified] = useState(false);

  if (!isOpen) return null;

  const handleNotify = () => {
    setNotified(true);
    setTimeout(() => {
      setNotified(false);
      onClose();
    }, 2000);
  };

  return (
    <Overlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <TopDecoration />
        <CloseButton onClick={onClose}>
          <FaTimes />
        </CloseButton>
        
        <Content>
          <IconWrapper>
            <FaRocket />
          </IconWrapper>
          
          <Title>Coming Soon!</Title>
          <Subtitle>Our new learning platform is almost ready</Subtitle>
          
          <Badge>🚀 UNDER DEVELOPMENT</Badge>
          
          <ProgressContainer>
            <ProgressBar />
          </ProgressContainer>
          <ProgressText>
            <span>Launch progress</span>
            <span>65%</span>
          </ProgressText>
          
          <FeatureList>
            <Feature>
              <FaArrowRight />
              <span>Interactive video lectures with quizzes</span>
            </Feature>
            <Feature>
              <FaArrowRight />
              <span>Live doubt clearing sessions</span>
            </Feature>
            <Feature>
              <FaArrowRight />
              <span>Practice exams & certification prep</span>
            </Feature>
            <Feature>
              <FaArrowRight />
              <span>Track your progress with analytics</span>
            </Feature>
          </FeatureList>
          
          <NotifyButton onClick={handleNotify}>
            {notified ? (
              <>✓ Notified!</>
            ) : (
              <>
                <FaBell /> Notify Me When Live
              </>
            )}
          </NotifyButton>
          
          <SmallText>
            Be the first to experience our revolutionary learning platform
          </SmallText>
        </Content>
      </ModalContainer>
    </Overlay>
  );
};

export default ComingSoonModal;